import {
  afterRenderEffect,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef, inject,
  Inject,
  input,
  NgZone,
  OnDestroy,
  output, Renderer2,
  Resource,
  ResourceStatus,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { PageComponent } from '../page/page.component';
import { PageHeaderComponent } from '../page/page-header/page-header.component';
import { MasterDetailComponent, UiCoreModule, WithBreakpoints } from '@softline/ui-core';
import { MenuOutletComponent } from '../../menu/menu-outlet/menu-outlet.component';
import { ResourceContainerComponent } from '../../components/resource-container/resource-container.component';
import { BackNavigable, backNavigation, BackNavigationService, NavigateBackOptions } from '@softline/application';

@Component({
    selector: 'soft-master-detail-page',
  imports: [
    CommonModule,
    PageComponent,
    PageHeaderComponent,
    UiCoreModule,
    MenuOutletComponent,
    ResourceContainerComponent
  ],
    templateUrl: './master-detail.page.html',
    styleUrl: './master-detail.page.scss'
})
export class MasterDetailPage<T>
  extends WithBreakpoints()
  implements OnDestroy, BackNavigable
{
  backNavigationService = new BackNavigationService();

  private masterDetail = viewChild<MasterDetailComponent<T>>('masterDetail');
  private masterDetailElem = viewChild('masterDetail', { read: ElementRef });
  private renderer2 = inject(Renderer2)

  resourceStates = ResourceStatus;

  #observer: ResizeObserver | undefined;

  masterHeaderTemplate = input<TemplateRef<any> | null>(null);
  masterTemplate = input<TemplateRef<any> | null>(null);
  detailTemplate = input<TemplateRef<any> | null>(null);
  detailPlaceholder = input<TemplateRef<any> | null>(null);
  masterLoadingTemplate = input<TemplateRef<any> | null>(null);

  loadingEntries = input<number>(5);

  resource = input.required<Resource<T[] | undefined>>();

  readonly #masterDetailOffsetTop = signal(0);

  readonly masterDetailMaxHeightPx = computed(() => {
    return (
      this.document.body.clientHeight - (this.#masterDetailOffsetTop() + 16)
    );
  });

  selectedChange = output<T | null>();

  header = contentChild(PageHeaderComponent, { descendants: true });

  selected = computed(() => {
    const masterDetail = this.masterDetail();
    if (masterDetail) return masterDetail.selected();
    return null;
  });

  select(index: number): void {
    const masterDetail = this.masterDetail();
    if (masterDetail)
      masterDetail.select(index);
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone
  ) {
    super();

    backNavigation(this);

    this.#observer = new ResizeObserver((entries) => {
      const offestTop = entries[0].target?.getBoundingClientRect().top;
      this.zone.run(() => {
        this.#masterDetailOffsetTop.set(offestTop);
      });
    });

    afterRenderEffect(() => {
      const masterDetailElement =
        this.masterDetailElem()?.nativeElement?.firstElementChild;

      if (!masterDetailElement) {
        return;
      }

      this.#observer?.observe(masterDetailElement);
    });
  }

  ngOnDestroy() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  async navigateBack(options?: NavigateBackOptions): Promise<void> {
    const smBreakpoint = this.smBreakpoint();
    if((!this.selected() || smBreakpoint) && options?.parent)
      await options.parent.navigateBack(options);
    else if (!smBreakpoint)
      this.select(-1)
  }
}
